import { Injectable, OnDestroy } from '@angular/core';
import { LiteratureDataService } from '@literature/services/literature-data.service';
import {
  IUserProfile,
  PersonalisationAPIService,
  SiteConfigService,
  UserRole,
} from '@services';
import { TranslateService } from '@shared/translate/translate.service';
import {
  LiteratureDocType,
  LiteratureDocument,
  LiteratureDocumentListingData,
} from '@types';
import { SH } from '@utils/app.constants';
import {
  createNavigationString,
  getFileNameFromUrl,
} from '@utils/text/string-utils';
import { combineLatest, Observable, ReplaySubject, Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';

export interface DocumentList {
  documents: DocumentEntity[];
  scrollId: string;
  viewAllTitle: string;
}

export interface DocumentEntity {
  title: string;
  customFilePath: string;
  docType: LiteratureDocType;
  tabId: string;
  href: string;
  previewLink: string;
  pdfLink: string;
  type: string;
  iconType: string;
  protected: boolean;
  publicationDate: string;
  hasPrimaryLanguage: boolean;
}

export interface DocumentsConfig extends Array<string> {}

const MAX_HEADER_ELEMENTS = 4;
const MAX_PREVIEW_ELEMENTS = 10;
@Injectable({
  providedIn: 'root',
})
export class HeaderDocumentsService implements OnDestroy {
  public documentList: DocumentList;

  private documentListingDataInput$: ReplaySubject<
    LiteratureDocumentListingData[]
  >;
  private documentConfig$: ReplaySubject<DocumentsConfig>;
  public previewConfig$: ReplaySubject<DocumentsConfig>;
  public documentListingData$: Observable<DocumentEntity[]>;
  public documentPreviewData$: Observable<DocumentEntity[]>;
  public showLogIn = true;
  private unsubscribe$: Subject<void> = new Subject<void>();
  private sitePrimaryLang: string;
  private isIdentifiedUser: boolean;

  constructor(
    private translateService: TranslateService,
    private literatureDataService: LiteratureDataService,
    private siteConfigService: SiteConfigService,
    private personalisationService: PersonalisationAPIService
  ) {
    // TODO - to confirm if this is correct source
    // copied from title- service
    this.sitePrimaryLang = this.siteConfigService
      .getPdsLanguage()
      ?.replace('_', '-');
    // initializes documents list
    this.documentListingDataInput$ = new ReplaySubject<
      LiteratureDocumentListingData[]
    >(1);

    // initializes documents config
    this.documentConfig$ = new ReplaySubject<DocumentsConfig>(1);
    this.previewConfig$ = new ReplaySubject<DocumentsConfig>(1);

    // reads data about logged user
    this.literatureDataService
      .getProfileData$()
      ?.pipe(
        takeUntil(this.unsubscribe$),
        filter((profile) => !!profile)
      )
      .subscribe((data: IUserProfile) => {
        this.showLogIn =
          !data?.isLoggedIn ||
          UserRole[data?.profileInfo?.role] === SH.toLocaleLowerCase();
      });
    this.personalisationService
      .isIdentified$()
      ?.pipe(takeUntil(this.unsubscribe$))
      ?.subscribe((isIdentified) => (this.isIdentifiedUser = isIdentified));

    // reads documents from literature service
    this.documentListingData$ = combineLatest([
      this.documentConfig$,
      this.previewConfig$,
      this.documentListingDataInput$,
    ]).pipe(
      takeUntil(this.unsubscribe$),
      map((result) => {
        return this.getDocuments(result[0], result[1], result[2]);
      })
    );

    // reads documents from literature service
    this.documentPreviewData$ = combineLatest([
      this.previewConfig$,
      this.documentListingDataInput$,
    ]).pipe(
      takeUntil(this.unsubscribe$),
      map((result) => {
        return this.getPreviewDocuments(result[0], result[1]);
      })
    );
  }

  /**
   * returns documents - filtered and sorted
   * @param documents array of documents
   * @param segmentId current segment
   */
  public getDocuments(
    config: DocumentsConfig,
    previewConfig: DocumentsConfig,
    documents: LiteratureDocumentListingData[]
  ): DocumentEntity[] {
    return documents
      .filter(({ literatureData }) => {
        return (
          config.includes(literatureData.dctermsType) &&
          this.isDocumentDownloadValid(literatureData)
        );
      })
      .map(({ literatureData }) => {
        const literatureDataMapped = this.mapDocumentObject(literatureData);
        // WDE 441 - redirect to preview instead download
        if (previewConfig.includes(literatureDataMapped.docType)) {
          literatureDataMapped.href = literatureDataMapped.previewLink;
        }
        return literatureDataMapped;
      })
      .sort(this.documentsSort(config))
      .filter((v, i, a) => a.findIndex((t) => t.docType === v.docType) === i) // filters only one document of specific type
      .slice(0, MAX_HEADER_ELEMENTS); // takes max 4 elements
  }

  /**
   * returns documents - filtered and sorted
   * @param documents array of documents
   * @param segmentId current segment
   */
  public getPreviewDocuments(
    config: DocumentsConfig,
    documents: LiteratureDocumentListingData[]
  ): DocumentEntity[] {
    return documents
      .filter(({ literatureData }) => {
        return (
          config.includes(literatureData.dctermsType) &&
          this.isDocumentDownloadValid(literatureData)
        );
      })
      .map(({ literatureData }) => {
        return this.mapDocumentObject(literatureData);
      })
      .sort(this.documentsSort(config))
      .filter((v, i, a) => a.findIndex((t) => t.docType === v.docType) === i) // filters only one document of specific type
      .slice(0, MAX_PREVIEW_ELEMENTS); // takes max 7 elements
  }

  /**
   *  maps lierature object to fund header object
   * @param literatureData source data
   */
  mapDocumentObject(literatureData: LiteratureDocument): DocumentEntity {
    const previewSuffix = createNavigationString(literatureData.dctermsType);

    return {
      title: `${this.translateService.instant(
        'literature.' + literatureData.dctermsType
      )}`,
      customFilePath: literatureData.customFilePath,
      docType: literatureData.dctermsType,
      tabId: previewSuffix,
      publicationDate: literatureData.publicationDate,
      href: this.getDownloadLink(literatureData),
      previewLink:
        literatureData?.shareclass?.previewLink + '#' + previewSuffix,
      pdfLink: literatureData.downloadUrl,
      type: literatureData.fileExtension,
      iconType: 'download',
      protected: this.isLoginRequired(literatureData),
      hasPrimaryLanguage: literatureData.dctermsLanguage?.includes(
        this.sitePrimaryLang
      ),
    };
  }

  private isLoginRequired(litData: LiteratureDocument): boolean {
    if (litData.clientUse && !this.isIdentifiedUser) {
      return (
        (!litData.clientUse || litData.clientUse === 'No') && this.showLogIn
      );
    } else {
      return false;
    }
  }
  /**
   * returns empty string if SIGNIN is required
   * @param litDocument literature document object
   */
  getDownloadLink(litDocument: LiteratureDocument) {
    return this.isLoginRequired(litDocument) ? '' : litDocument.pdfDownloadLink;
  }

  /**
   * sorts by config order and then by publicationDate desc
   * @param literature header config
   */
  private documentsSort = (config) => (a, b) => {
    const a1 = config.indexOf(a.docType);
    const a2 = config.indexOf(b.docType);
    const b1 = a.publicationDate;
    const b2 = b.publicationDate;
    const c1 = a.hasPrimaryLanguage ? 1 : 0;
    const c2 = b.hasPrimaryLanguage ? 1 : 0;

    return a1 - a2 || b2?.localeCompare(b1) || c2 - c1;
  };

  /**
   * verifies if link is valid - this method may be extended in the future
   */
  private isDocumentDownloadValid(litDocument: LiteratureDocument) {
    return (
      litDocument?.pdfDownloadLink?.length > 1 &&
      litDocument?.docId &&
      litDocument.downloadUrl &&
      this.isMappedLanguage(litDocument)
    );
  }

  // This method checks weether it is a mapped language that matched the documents urls.
  private isMappedLanguage(litDocument: LiteratureDocument): boolean {
    /* This will return true always for the sites where language mapping is not set
    // to replicate the previous behaviour.
    */

    if (!this.siteConfigService.literature?.languageMapping?.length) {
      return true;
    }

    // The language mapping can not be validate if downloadUrl or literatureHref is not in the document.
    if (!(litDocument.downloadUrl || litDocument.literatureHref)) {
      return false;
    }

    return this.siteConfigService.literature.languageMapping?.some((lang) =>
      litDocument.dctermsLanguage
        ?.map((v) => v.toLowerCase())
        .includes(lang.toLowerCase())
    );
  }

  /**
   *  updates documents with data read from literature service
   */
  public updateDocuments(listingData: LiteratureDocumentListingData[]) {
    this.documentListingDataInput$.next(listingData);
  }

  /**
   * updates config with data read from literature service
   */
  public updateConfig(config: DocumentsConfig) {
    this.documentConfig$.next(config);
  }

  /**
   * updates config with data read from literature service
   */
  public updatePreviewConfig(config: DocumentsConfig) {
    this.previewConfig$.next(config);
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
